import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { AlertTriangle, Eye, Plus } from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { AdvanceAmountsCell } from "@/components/requests/AdvanceAmountsCell";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PHONE_DIALOG_FOOTER, PHONE_SHEET_DIALOG } from "@/lib/responsive";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  AdvanceAmountsField,
  advanceAmountsError
} from "@/components/requests/AdvanceAmountsField";
import { Combobox } from "@/components/Combobox";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DateTimePicker } from "@/components/DateTimePicker";
import { SettlementAmountCell } from "@/components/requests/SettlementAmountCell";
import { DataTable } from "@/components/data-table/DataTable";
import { SortableHeader } from "@/components/data-table/SortableHeader";
import { type AppTableFeatures } from "@/components/data-table/features";
import SegmentedTabs from "@/components/SegmentedTabs";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { RefreshButton } from "@/components/RefreshButton";
import { SummaryRow } from "@/components/SummaryRow";
import { AdvanceDetailDialog } from "@/components/requests/AdvanceDetailDialog";
import { FinanceNoticeDialog } from "@/components/requests/FinanceNoticeDialog";
import { DownloadReportButton } from "@/components/requests/DownloadReportButton";
import { RequestDetailDialog } from "@/components/requests/RequestDetailDialog";
import { TravelTypePicker } from "@/components/requests/TravelTypePicker";
import { TripCell } from "@/components/requests/TripCell";
import { TripLocationFields } from "@/components/requests/TripLocationFields";
import { billTotals } from "@/lib/bills";
import { formatDate, formatDateTime, toIso } from "@/lib/datetime";
import {
  EMPTY_TRIP_LOCATION,
  tripLocationPayload,
  tripRoute,
  TripLocationValue,
  validateTripLocation
} from "@/lib/trip";
import {
  ApiError,
  createAdvance,
  discardMyRequest,
  fetchAdvance,
  fetchAdvanceLimit,
  fetchAllowedCountries,
  FinanceApprover,
  fetchFinanceApprovers,
  fetchMyAdvances,
  fetchMyRequests,
  fetchProfile,
  resubmitMyAdvance
} from "../api";
import {
  Advance,
  AdvanceAmount,
  AdvanceStatus,
  AllowedCountry,
  CLAIM_METHOD_LABEL,
  ClaimMethod,
  TRAVEL_REQUEST_STATUS_LABEL,
  TRAVEL_TYPE_LABEL,
  TravelRequestStatus,
  TravelRequestSummary,
  TravelType
} from "../types";

type View = "requests" | "advances";
type StatusFilter = "All" | TravelRequestStatus;
type AdvanceStatusFilter = "All" | AdvanceStatus;

const EMPTY_COPY: Record<StatusFilter, string> = {
  All: "No travel requests yet. Create one to get started.",
  Draft: "No draft requests.",
  Submitted: "No submitted requests awaiting a decision.",
  PendingFinance: "No requests awaiting Finance Approver sign-off.",
  Approved: "No approved requests yet.",
  Rejected: "No rejected requests.",
  Completed: "No completed trips yet.",
  ResubmitRequested: "Nothing needs changes right now."
};

// Employee-facing wording for an advance's status ("Resubmit" reads as an
// instruction, not a state). Colour comes from the shared StatusBadge.
const ADVANCE_STATUS_LABEL: Record<AdvanceStatus, string> = {
  Pending: "Pending",
  Approved: "Approved",
  Rejected: "Rejected",
  Paid: "Paid",
  Resubmit: "Needs changes"
};

const ADVANCE_EMPTY_COPY: Record<AdvanceStatusFilter, string> = {
  All: "No advance requests yet. Raise one to get started.",
  Pending: "No pending advances.",
  Approved: "No approved-but-unpaid advances.",
  Rejected: "No rejected advances.",
  Paid: "No paid advances yet.",
  Resubmit: "Nothing needs changes right now."
};

// The Duration column used to re-derive this itself from startDate/endDate
// as a plain calendar-midnight-to-midnight span (always +1, no half-days) -
// a trip departing 9:00 AM and arriving 9:00 AM the next day showed "2
// days" even though db/travel_duration.py's actual rule (what the policy
// allowance is computed from) counts that as exactly 1 full day: a trip is
// a rolling 24-hour window from the real departure TIME, not which
// calendar dates it touches. fullDayCount/halfDayCount are that same
// server-computed, policy-accurate value already stored on the request -
// reusing them here keeps this column in agreement with everything else
// that describes the trip's length (RequestDetailDialog's "Full / half
// days", the allowance preview) instead of a second, divergent number.
function formatDuration(fullDayCount: number, halfDayCount: number): string {
  const n = fullDayCount + halfDayCount * 0.5;
  return `${n} day${n === 1 ? "" : "s"}`;
}

const columnHelper = createColumnHelper<
  AppTableFeatures,
  TravelRequestSummary
>();
const advanceColumnHelper = createColumnHelper<AppTableFeatures, Advance>();

// Valid values for the ?status=/?advanceStatus= query params this page
// reads on mount - the Dashboard's stat-card tiles link here with these so
// e.g. "Awaiting approval" lands directly on the filtered table instead of
// the employee having to re-pick the filter themselves (see App.tsx's
// routing note / Dashboard.tsx's StatCard `to` props). Deliberately TWO
// separate params, not one shared `status` - Travel Request and Advance
// statuses aren't the same vocabulary (an advance has no "PendingFinance"
// or "Completed"; "Approved" means something different for each: a
// travel request's Approved is its final state, an advance's Approved is
// still awaiting disbursement, only Paid is final) - collapsing them onto
// one param either silently failed validation for one side or, worse,
// silently applied the WRONG matching status to the wrong tab.
const VALID_STATUS_PARAMS = new Set<StatusFilter>([
  "All",
  "Draft",
  "Submitted",
  "PendingFinance",
  "Approved",
  "Rejected",
  "Completed",
  "ResubmitRequested"
]);
const VALID_ADVANCE_STATUS_PARAMS = new Set<AdvanceStatusFilter>([
  "All",
  "Pending",
  "Approved",
  "Rejected",
  "Paid",
  "Resubmit"
]);

export default function MyRequests() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?advance=<id> (a notification deep-link, see notifications.py's
  // _dispatch) must also land on the Advances tab, same as the explicit
  // ?view=advances this already handled - otherwise closing the deep-linked
  // advance's detail sheet would show the Travel Requests table instead of
  // the one actually holding that row (the exact bug MyApprovals.tsx had).
  const initialView: View =
    searchParams.get("view") === "advances" || searchParams.get("advance")
      ? "advances"
      : "requests";
  const initialStatusParam = searchParams.get("status");
  const initialStatus: StatusFilter = VALID_STATUS_PARAMS.has(
    initialStatusParam as StatusFilter
  )
    ? (initialStatusParam as StatusFilter)
    : "All";
  const initialAdvanceStatusParam = searchParams.get("advanceStatus");
  const initialAdvanceStatus: AdvanceStatusFilter =
    VALID_ADVANCE_STATUS_PARAMS.has(
      initialAdvanceStatusParam as AdvanceStatusFilter
    )
      ? (initialAdvanceStatusParam as AdvanceStatusFilter)
      : "All";

  const [view, setView] = React.useState<View>(initialView);

  const [rows, setRows] = React.useState<TravelRequestSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState<StatusFilter>(initialStatus);

  const [advanceRows, setAdvanceRows] = React.useState<Advance[]>([]);
  const [advancesLoading, setAdvancesLoading] = React.useState(true);
  const [advanceStatus, setAdvanceStatus] =
    React.useState<AdvanceStatusFilter>(initialAdvanceStatus);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  // Set when the Sheet is open to EDIT an existing Resubmit advance
  // (clicked "Edit & resubmit" below) rather than raise a new one - null
  // means the normal create flow. The Sheet's fields/validation/review step
  // are identical either way; only the final submit action differs (see
  // confirmSubmitAdvance) and the title/description copy.
  const [editingAdvanceId, setEditingAdvanceId] = React.useState<string | null>(
    null
  );
  const [editingAdvanceLoading, setEditingAdvanceLoading] =
    React.useState(false);
  // The approver's "what needs to change" note for the advance being edited
  // & resubmitted - shown as a banner in the edit dialog so the requester
  // can see why it came back. Null unless the advance carries one.
  const [advResubmitNote, setAdvResubmitNote] = React.useState<{
    stage: "Manager" | "Finance";
    comment: string;
  } | null>(null);
  // The confirmation dialog shown over the form - Submit no longer submits
  // directly, it opens this summary of what's being requested first (see
  // handleSubmitAdvanceClick/confirmSubmitAdvance below). Reset whenever the
  // sheet is closed or reopened, same as every other advance-form field.
  const [reviewOpen, setReviewOpen] = React.useState(false);
  // One line per currency requested (multi-currency advances) - see
  // AdvanceAmountsField, which keeps the policy currency as the first row.
  const [advAmounts, setAdvAmounts] = React.useState<AdvanceAmount[]>([]);
  const [reason, setReason] = React.useState("");
  // Fixed Allowance or Reimbursement, same Step 1 choice the New Request
  // wizard makes - now made up front for an advance too, since the amount
  // is capped against THAT scheme's entitlement (previously an advance
  // picked no claim method and was always capped at the lower of both
  // schemes, which under-capped a Fixed Allowance advance to
  // Reimbursement's usually-smaller per-day limit). Decides which scheme's
  // country list is offered, same as the wizard.
  const [advClaimMethod, setAdvClaimMethod] = React.useState<ClaimMethod | "">(
    ""
  );
  // The explicit Domestic/International choice, and the locations it needs
  // (country + start/end city, or starting + destination country) - same
  // shape the New Request wizard uses, so an advance and the request it is
  // later linked to line up field for field.
  const [advTravelType, setAdvTravelType] = React.useState<TravelType | "">("");
  const [advLocation, setAdvLocation] =
    React.useState<TripLocationValue>(EMPTY_TRIP_LOCATION);
  const [advDepartureAt, setAdvDepartureAt] = React.useState("");
  const [advArrivalAt, setAdvArrivalAt] = React.useState("");
  const [advFlightDetails, setAdvFlightDetails] = React.useState("");
  const [advFinanceApproverId, setAdvFinanceApproverId] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const resetAdvanceForm = () => {
    setAdvAmounts([]);
    setReason("");
    setAdvClaimMethod("");
    setAdvTravelType("");
    setAdvLocation(EMPTY_TRIP_LOCATION);
    setAdvDepartureAt("");
    setAdvArrivalAt("");
    setAdvFlightDetails("");
    setAdvFinanceApproverId("");
    setReviewOpen(false);
    setEditingAdvanceId(null);
    setAdvResubmitNote(null);
  };

  // Opens the Sheet pre-filled with an existing Resubmit advance's fields,
  // instead of blank - "Edit & resubmit" below. Mirrors the toLocalDatetime
  // conversion NewRequest.tsx's own draft-resume effect does for the same
  // reason (DateTimePicker's value is a local datetime-local string, the
  // API gives back ISO). financeApproverId isn't resend-able here since
  // fetchAdvance doesn't return it as an editable pick (only the
  // snapshotted name/email) - the original pick stays whatever it was,
  // same as a travel request's Resubmit not re-asking for the Finance
  // Approver either.
  React.useEffect(() => {
    if (!editingAdvanceId) return;
    setEditingAdvanceLoading(true);
    setError(null);
    fetchAdvance(editingAdvanceId)
      .then((a) => {
        setAdvAmounts(a.amounts);
        setReason(a.reason || "");
        setAdvClaimMethod(a.claimMethod || "");
        setAdvTravelType(a.travelType || "");
        setAdvLocation({
          country: a.country,
          originCountry: a.originCountry || "",
          startCity: a.startCity || "",
          endCity: a.endCity || ""
        });
        setAdvDepartureAt(a.departureAt.slice(0, 16));
        setAdvArrivalAt(a.arrivalAt.slice(0, 16));
        setAdvFlightDetails(a.flightDetails || "");
        setAdvResubmitNote(
          a.resubmitComment
            ? {
                stage:
                  a.resubmitRequestedBy === "Finance" ? "Finance" : "Manager",
                comment: a.resubmitComment
              }
            : null
        );
        setDialogOpen(true);
      })
      .catch(() => {
        setError("Could not load this advance for editing.");
        setEditingAdvanceId(null);
      })
      .finally(() => setEditingAdvanceLoading(false));
  }, [editingAdvanceId]);

  // Only actually used server-side when the requester has no reporting
  // manager on file (create_advance falls back straight to Finance in
  // that case) - shown unconditionally here since the frontend has no way
  // to know that ahead of submission, same tolerance as the New Request
  // wizard's identical picker (harmlessly ignored server-side otherwise).
  const [financeApprovers, setFinanceApprovers] = React.useState<
    FinanceApprover[]
  >([]);
  const [financeApproversLoading, setFinanceApproversLoading] =
    React.useState(true);
  React.useEffect(() => {
    fetchFinanceApprovers()
      .then(setFinanceApprovers)
      .catch(() => setFinanceApprovers([]))
      .finally(() => setFinanceApproversLoading(false));
  }, []);

  // Designation drives the policy limit below (same designation-tiered
  // Reimbursement lookup a travel request uses) - read-only, sourced
  // from the signed-in user's own Entra profile, never free-typed. Not
  // sent to the server: create_advance() re-resolves it itself from the
  // user's stored designation, so this is display-only.
  const [designation, setDesignation] = React.useState("");
  // Drives the "Notify Finance" Cc popup (see financeNoticeOpen below): a
  // requester with no reporting manager on file has their advance skip
  // straight to the Finance Approver they pick, with no separate Manager-
  // approve step to show that popup at - same gap and same fix as
  // NewRequest.tsx. Defaults true (don't show the popup) until this
  // resolves, so a slow/failed fetch fails toward the existing
  // silent-submit behaviour.
  const [hasReportingManager, setHasReportingManager] = React.useState(true);
  // Display-only, for the Review step - who this advance's Manager stage
  // will actually route to. Null while hasReportingManager is still
  // resolving, or when there isn't one.
  const [reportingManagerName, setReportingManagerName] = React.useState<
    string | null
  >(null);
  React.useEffect(() => {
    fetchProfile()
      .then((profile) => {
        if (profile.jobTitle) setDesignation(profile.jobTitle);
        setHasReportingManager(!!profile.hasReportingManager);
        setReportingManagerName(profile.reportingManagerName ?? null);
      })
      .catch(() => {});
  }, []);

  // An advance can later be linked only to a request of the SAME claim
  // method and the same travel type. So the country picker offers exactly
  // what that one scheme has configured for the chosen Domestic/
  // International type - same restriction and same "contact admin"
  // fallback as the New Request wizard's own picker. Nothing is fetched
  // until both a claim method and a travel type are chosen.
  const [allowedCountries, setAllowedCountries] = React.useState<
    AllowedCountry[]
  >([]);
  const [allowedCountriesLoading, setAllowedCountriesLoading] =
    React.useState(false);
  React.useEffect(() => {
    if (!advClaimMethod || !advTravelType) {
      setAllowedCountries([]);
      setAllowedCountriesLoading(false);
      return;
    }
    let cancelled = false;
    setAllowedCountriesLoading(true);
    fetchAllowedCountries(advTravelType, advClaimMethod)
      .then((list) => {
        if (!cancelled) setAllowedCountries(list);
      })
      .catch(() => {
        if (!cancelled) setAllowedCountries([]);
      })
      .finally(() => {
        if (!cancelled) setAllowedCountriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [advClaimMethod, advTravelType]);
  const advCurrency =
    allowedCountries.find((c) => c.country === advLocation.country)?.currency ||
    "";

  // Currencies an advance row may use: those of the countries policy
  // configures for this employee's designation (or the org default) under
  // the chosen claim method + trip type - the Country picker's own list.
  const advAllowedCurrencies = React.useMemo(
    () => Array.from(new Set(allowedCountries.map((c) => c.currency))).sort(),
    [allowedCountries]
  );

  // Changing the claim method or the travel type changes the countries on
  // offer and the locations asked for, so the location answers start over.
  const chooseAdvClaimMethod = (claimMethod: ClaimMethod) => {
    if (claimMethod === advClaimMethod) return;
    setAdvClaimMethod(claimMethod);
    setAdvLocation(EMPTY_TRIP_LOCATION);
  };
  const chooseAdvTravelType = (travelType: TravelType) => {
    if (travelType === advTravelType) return;
    setAdvTravelType(travelType);
    setAdvLocation(EMPTY_TRIP_LOCATION);
  };

  // Live cap for the trip currently entered - that claim method's own
  // entitlement for this country and travel type (the server's
  // _advance_entitlement), fetched fresh whenever the trip changes so the
  // employee sees the cap before they hit submit. The server enforces this
  // independently in create_advance() - this is purely so the employee
  // isn't surprised by a 400 after filling the whole form. A policy gap
  // (nothing usable configured for this country) comes back as an error
  // message for the admin, shown in place of the cap.
  const [advanceLimits, setAdvanceLimits] = React.useState<Record<
    string,
    number
  > | null>(null);
  const [advanceLimitError, setAdvanceLimitError] = React.useState<
    string | null
  >(null);
  const [limitLoading, setLimitLoading] = React.useState(false);
  React.useEffect(() => {
    if (
      !advClaimMethod ||
      !advTravelType ||
      !advLocation.country ||
      !advDepartureAt ||
      !advArrivalAt ||
      new Date(advArrivalAt) <= new Date(advDepartureAt)
    ) {
      setAdvanceLimits(null);
      setAdvanceLimitError(null);
      return;
    }
    let cancelled = false;
    setLimitLoading(true);
    fetchAdvanceLimit({
      travelType: advTravelType,
      claimMethod: advClaimMethod,
      country: advLocation.country,
      departureAt: toIso(advDepartureAt),
      arrivalAt: toIso(advArrivalAt)
    })
      .then((result) => {
        if (!cancelled) {
          setAdvanceLimits(result.limitsByCurrency);
          setAdvanceLimitError(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setAdvanceLimits(null);
        setAdvanceLimitError(err instanceof ApiError ? err.message : null);
      })
      .finally(() => {
        if (!cancelled) setLimitLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    advClaimMethod,
    advTravelType,
    advLocation.country,
    advDepartureAt,
    advArrivalAt
  ]);

  const load = React.useCallback(() => {
    setLoading(true);
    fetchMyRequests()
      .then(setRows)
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);

  // The same request/advance open in two tabs (or acted on elsewhere) can
  // move past the status an action here expects by the time it's sent - the
  // server 409s (StaleStatusError or an equivalent guard, e.g.
  // discard_draft's/resubmit_my_advance's own ValueError) instead of
  // silently succeeding twice or corrupting state. Closes whatever dialog
  // was open, toasts the server's actual explanation, and reloads so the
  // table reflects what really happened - same handling as
  // MyApprovals.tsx/ApproverQueue.tsx's identical helper.
  const handleStaleDecision = (err: unknown, reload: () => void) => {
    if (err instanceof ApiError && err.status === 409) {
      toast.error(
        err.message ||
          "This has already changed (maybe from another tab, or an approver acted on it) - the list has been refreshed to show its current status."
      );
      reload();
      return true;
    }
    return false;
  };

  // "New request" reminder - a Draft is created once the wizard reaches the
  // Bills step (either claim method, see NewRequest.tsx), so any Draft row
  // here represents a genuinely abandoned in-progress request. Surfaced instead of silently creating another one, so the
  // user can pick up where they left off rather than piling up duplicates.
  const draftRows = React.useMemo(
    () =>
      rows
        .filter((r) => r.status === "Draft")
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [rows]
  );
  const [showDraftReminder, setShowDraftReminder] = React.useState(false);
  const handleNewRequestClick = () => {
    if (draftRows.length > 0) {
      setShowDraftReminder(true);
    } else {
      // No known server Draft to resume - force-clear any leftover
      // sessionStorage snapshot from an earlier abandoned attempt (e.g. one
      // whose Draft was since discarded elsewhere) so NewRequest.tsx starts
      // genuinely blank instead of silently reviving stale state.
      navigate("/my-requests/new?fresh=1");
    }
  };

  // "View details" dialogs - read-only, same RequestDetailDialog/
  // AdvanceDetailDialog MyApprovals.tsx/ApproverQueue.tsx already use
  // before an approver decides; reused here so an employee can see
  // everything they submitted (bills, trip, linked advance, etc.) without
  // this table itself needing to carry every one of those fields.
  // Deep-link support: a notification's "View Request" button appends
  // ?request=<id> or ?advance=<id> (see notifications.py's _dispatch) -
  // same reasoning/pattern as MyApprovals.tsx's identical handling.
  const [viewRequestId, setViewRequestId] = React.useState<string | null>(
    () => searchParams.get("request")
  );
  const [viewAdvanceId, setViewAdvanceId] = React.useState<string | null>(
    () => searchParams.get("advance")
  );
  const [highlightOnOpen, setHighlightOnOpen] = React.useState(
    () => !!searchParams.get("request") || !!searchParams.get("advance")
  );

  const [discardTarget, setDiscardTarget] =
    React.useState<TravelRequestSummary | null>(null);
  const [discarding, setDiscarding] = React.useState(false);
  const confirmDiscard = async () => {
    if (!discardTarget) return;
    setDiscarding(true);
    try {
      await discardMyRequest(discardTarget.id);
      setDiscardTarget(null);
      load();
      toast.success("Draft discarded");
    } catch (err) {
      // E.g. this Draft was already discarded/finalized in another tab -
      // handleStaleDecision toasts the server's own explanation, closes
      // the dialog and reloads; any other failure leaves it open to retry.
      if (!handleStaleDecision(err, load)) {
        toast.error(
          err instanceof ApiError
            ? err.message
            : "Could not discard this draft - check your connection and try again."
        );
      } else {
        setDiscardTarget(null);
      }
    } finally {
      setDiscarding(false);
    }
  };

  const loadAdvances = React.useCallback(() => {
    setAdvancesLoading(true);
    fetchMyAdvances()
      .then(setAdvanceRows)
      .finally(() => setAdvancesLoading(false));
  }, []);
  React.useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  const data = React.useMemo(
    () => (status === "All" ? rows : rows.filter((r) => r.status === status)),
    [rows, status]
  );

  const advanceData = React.useMemo(
    () =>
      advanceStatus === "All"
        ? advanceRows
        : advanceRows.filter((r) => r.status === advanceStatus),
    [advanceRows, advanceStatus]
  );

  const validateAdvance = (): string | null => {
    if (!advClaimMethod)
      return "Select whether this advance is for Fixed Allowance or Reimbursement.";
    if (!advTravelType)
      return "Select whether this trip is Domestic or International.";
    const locationError = validateTripLocation(advTravelType, advLocation);
    if (locationError) return locationError;
    if (!advDepartureAt || !advArrivalAt)
      return "Departure and Arrival are required.";
    if (new Date(advArrivalAt) <= new Date(advDepartureAt))
      return "Arrival must be after departure.";
    if (!advFlightDetails.trim())
      return "Enter the purpose of travel.";
    // Only the policy-currency row is checked against advAllowedCurrencies/
    // advanceLimits - every other ("also received") row is free (the server
    // re-checks all of this the same way).
    const amountsError = advanceAmountsError(
      advAmounts,
      advanceLimits,
      advAllowedCurrencies,
      advCurrency
    );
    if (amountsError) return amountsError;
    // Same rule as the request wizard: only enforced when at least one
    // Finance Approver is configured. The pick is what routes the advance to
    // that one person's Approver Queue - left blank it would fall back to
    // every Finance Approver seeing it. Skipped while editing an existing
    // (Resubmit) advance - resubmitMyAdvance routes back to whichever stage
    // already sent it back, reusing its existing approval record, so this
    // pick is neither shown nor re-sent in that flow (same as a travel
    // request's Resubmit not re-asking who the Finance Approver is).
    if (
      !editingAdvanceId &&
      !financeApproversLoading &&
      financeApprovers.length > 0 &&
      !advFinanceApproverId
    ) {
      return "Select a Finance Approver.";
    }
    return null;
  };

  // Shown instead of submitting directly when the requester has no
  // reporting manager (see hasReportingManager above) - same popup and
  // same reasoning as NewRequest.tsx's financeNoticeOpen.
  const [financeNoticeOpen, setFinanceNoticeOpen] = React.useState(false);
  const [financeCc, setFinanceCc] = React.useState<string[]>([]);

  const submitAdvance = async (ccEmails?: string[]) => {
    if (!advClaimMethod || !advTravelType) return;
    setSaving(true);
    setError(null);
    try {
      const result = await createAdvance({
        amounts: advAmounts,
        claimMethod: advClaimMethod,
        reason: reason || undefined,
        ...tripLocationPayload(advTravelType, advLocation),
        departureAt: toIso(advDepartureAt),
        arrivalAt: toIso(advArrivalAt),
        flightDetails: advFlightDetails || undefined,
        financeApproverId: advFinanceApproverId || undefined,
        ccEmails
      });
      setDialogOpen(false);
      setFinanceNoticeOpen(false);
      resetAdvanceForm();
      loadAdvances();
      // No reporting manager on file - this advance's approval was routed
      // straight to the chosen Finance Approver instead of a Manager (see
      // requests_api.py's create_advance). Surfaced here since it's
      // otherwise invisible to the requester.
      if (result.skippedToFinance) {
        toast.success(
          "Submitted - you have no reporting manager on file, so this went straight to your Finance Approver."
        );
      } else {
        toast.success("Advance request submitted");
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not submit this advance request - check your connection and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // The form's own Submit - validates, then opens the confirmation dialog
  // rather than submitting directly. confirmSubmitAdvance (below) is what
  // actually submits, once the requester has reviewed and confirmed.
  const handleSubmitAdvanceClick = () => {
    // Toasted (top-right, red - see components/ui/sonner.tsx), not the
    // inline message this used to set - that rendered at the end of the
    // Sheet's own scrollable content, below Reason, so clicking Submit from
    // a field higher up the form showed an error invisible until scrolling
    // all the way down to find it. submitAdvance/submitResubmitAdvance's own
    // API-failure errors (setError elsewhere in this file) are untouched -
    // those still show inline, both here and in the confirm review dialog,
    // and are also what FinanceNoticeDialog's own `error` prop displays.
    const validationError = validateAdvance();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setError(null);
    setReviewOpen(true);
  };

  // Resubmitting an existing advance - no Notify Finance popup (there's no
  // separate "notify" moment on a resubmit, same as a travel request's
  // employee-resubmit route), just updates the same document in place and
  // routes back to whichever stage sent it back.
  const submitResubmitAdvance = async () => {
    if (!editingAdvanceId || !advClaimMethod || !advTravelType) return;
    setSaving(true);
    setError(null);
    try {
      await resubmitMyAdvance(editingAdvanceId, {
        amounts: advAmounts,
        // Re-sent so a resubmit that changed the claim method is checked
        // against (and stored with) the right scheme - previously omitted.
        claimMethod: advClaimMethod,
        reason: reason || undefined,
        ...tripLocationPayload(advTravelType, advLocation),
        departureAt: toIso(advDepartureAt),
        arrivalAt: toIso(advArrivalAt),
        flightDetails: advFlightDetails || undefined
      });
      setDialogOpen(false);
      resetAdvanceForm();
      loadAdvances();
      toast.success("Advance resubmitted");
    } catch (err) {
      // E.g. this advance was already resubmitted/decided elsewhere (its
      // status moved past Resubmit) - handleStaleDecision toasts the
      // server's own explanation, closes the dialog and reloads; any other
      // failure keeps the form open with the usual inline error.
      if (!handleStaleDecision(err, loadAdvances)) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Could not resubmit this advance - check your connection and try again."
        );
      } else {
        setDialogOpen(false);
        resetAdvanceForm();
      }
    } finally {
      setSaving(false);
    }
  };

  // The review screen's confirm action - opens the Notify Finance Cc popup
  // first for a manager-less requester (that popup's own Send is what
  // actually calls submitAdvance(), see below), otherwise submits straight
  // away. Same branch handleSubmitAdvanceClick used to run directly, before
  // the review step existed. Editing an existing (Resubmit) advance skips
  // all of that - see submitResubmitAdvance above.
  const confirmSubmitAdvance = () => {
    if (editingAdvanceId) {
      submitResubmitAdvance();
      return;
    }
    if (!hasReportingManager) {
      // Closes the confirmation and the request-details sheet and opens the
      // Notify Finance popup in their place - the form's own state
      // (amounts, locations, etc.) is untouched, only used once more when
      // that popup's Send calls submitAdvance() below. Same "one popup
      // visible at a time" handoff as MyApprovals.tsx's decision ->
      // financeNotice.
      setReviewOpen(false);
      setDialogOpen(false);
      setFinanceCc([]);
      setFinanceNoticeOpen(true);
      return;
    }
    submitAdvance();
  };

  const columns = React.useMemo(
    () =>
      columnHelper.columns([
        // The accessor returns the whole route (not just `country`) so the
        // toolbar's "Search destination…" also matches a city or the
        // starting country, and sorting follows what's displayed.
        columnHelper.accessor((row) => tripRoute(row), {
          id: "country",
          meta: { label: "Trip" },
          header: ({ column }) => (
            <SortableHeader column={column}>Trip</SortableHeader>
          ),
          cell: ({ row }) => <TripCell trip={row.original} />
        }),
        columnHelper.accessor((row) => row.flightDetails || "", {
          id: "purpose",
          meta: { label: "Purpose" },
          header: "Purpose",
          cell: ({ row }) => (
            <span className="block max-w-[13.75rem] truncate text-sm" title={row.original.flightDetails || undefined}>
              {row.original.flightDetails || "-"}
            </span>
          )
        }),
        // Domestic/International (shown under the Trip column, via TripCell)
        // and Fixed Allowance/Reimbursement are two independent choices
        // (see CLAUDE.md's "trip type and locations" row) - a row showing
        // only the former left no way to tell which scheme it was raised
        // under at a glance.
        columnHelper.accessor("claimMethod", {
          id: "claimMethod",
          meta: { label: "Allowance Type" },
          header: ({ column }) => (
            <SortableHeader column={column}>Allowance Type</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="text-sm">
              {CLAIM_METHOD_LABEL[row.original.claimMethod]}
            </span>
          )
        }),
        // Actual spend (the sum of the trip's own bills) - not the
        // allowance/estimate, which is already shown inside the detail sheet
        // (see DetailCard.tsx's identical billTotals() use). Fixed Allowance
        // bills are tracking-only but still count here, same as everywhere
        // else actual spend is surfaced. Bills can span more than one
        // currency (e.g. mostly the request's own currency, a couple in
        // another) - every currency gets its own total, request currency
        // first, rather than only showing one and silently dropping the
        // rest. Sorted by the request-currency total alone (the primary,
        // always-present figure) - sorting meaningfully by a row that may
        // hold several unrelated currency totals isn't well-defined.
        columnHelper.accessor(
          (row) =>
            billTotals(
              row.expenses,
              row.currency,
              row.estimatedAllowanceAmount,
              row.policyLimitsByCurrencyAtSubmission
            ).totalInRequestCurrency,
          {
            id: "amount",
            meta: { label: "Amount" },
            header: ({ column }) => (
              <SortableHeader column={column}>Amount</SortableHeader>
            ),
            cell: ({ row }) => {
              const totals = billTotals(
                row.original.expenses,
                row.original.currency,
                row.original.estimatedAllowanceAmount,
                row.original.policyLimitsByCurrencyAtSubmission
              );
              const lines = [
                ...(totals.totalInRequestCurrency > 0
                  ? [`${row.original.currency} ${totals.totalInRequestCurrency.toLocaleString()}`]
                  : []),
                ...totals.others.map(
                  (o) => `${o.currency} ${o.total.toLocaleString()}`
                )
              ];
              return lines.length > 0 ? (
                <div className="whitespace-nowrap text-sm">
                  {lines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              ) : (
                <span className="text-sm">-</span>
              );
            }
          }
        ),
        // The real amount still owed to (or by) the employee, once any
        // linked advance is netted off - the same "Net payable to the
        // employee" figure CurrencyConversionCard's bottom tile shows in
        // the Request Details dialog, collapsed into one number in the
        // request's own currency (see lib/settlementAmount.ts). Not
        // sortable - it resolves asynchronously per row (a linked advance's
        // amounts, and for a multi-currency Reimbursement, live FX rates),
        // so there's no synchronous value to sort by without fetching
        // every row's figure up front.
        columnHelper.display({
          id: "settlementAmount",
          meta: { label: "Settlement amount" },
          header: "Settlement amount",
          cell: ({ row }) => (
            <SettlementAmountCell
              requestId={row.original.id}
              claimMethod={row.original.claimMethod}
              currency={row.original.currency}
              expenses={row.original.expenses}
              estimatedAllowanceAmount={row.original.estimatedAllowanceAmount}
              departureDate={row.original.departureAt || row.original.createdAt}
              linkedAdvanceId={row.original.linkedAdvanceId}
              settlementTargetByCurrency={row.original.settlementTargetByCurrency}
              fxOverrides={row.original.fxOverrides}
            />
          )
        }),
        columnHelper.accessor("startDate", {
          id: "dates",
          meta: { label: "Travel dates" },
          sortFn: "datetime",
          header: ({ column }) => (
            <SortableHeader column={column}>Travel dates</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm">
              {formatDate(row.original.startDate)} –{" "}
              {formatDate(row.original.endDate)}
            </span>
          )
        }),
        columnHelper.display({
          id: "duration",
          meta: { label: "Duration" },
          header: "Duration",
          cell: ({ row }) => (
            <span className="text-sm text-muted-foreground">
              {formatDuration(
                row.original.fullDayCount,
                row.original.halfDayCount
              )}
            </span>
          )
        }),
        columnHelper.accessor("status", {
          id: "status",
          meta: { label: "Status" },
          header: ({ column }) => (
            <SortableHeader column={column}>Status</SortableHeader>
          ),
          cell: ({ row }) => (
            <StatusBadge
              status={row.original.status}
              label={TRAVEL_REQUEST_STATUS_LABEL[row.original.status]}
            />
          )
        }),
        columnHelper.accessor("createdAt", {
          id: "created",
          meta: { label: "Created" },
          sortFn: "datetime",
          header: ({ column }) => (
            <SortableHeader column={column}>Created</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {formatDate(row.original.createdAt)}
            </span>
          )
        }),
        columnHelper.display({
          id: "actions",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const r = row.original;
            if (r.status === "Draft") {
              return (
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={() =>
                      navigate(
                        `/my-requests/new?draftId=${encodeURIComponent(r.id)}`
                      )
                    }
                  >
                    Continue
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={() => setDiscardTarget(r)}
                  >
                    Discard
                  </Button>
                </div>
              );
            }
            if (r.status === "ResubmitRequested") {
              // Reuses the same resume machinery as a Draft's "Continue"
              // (NewRequest.tsx's draftId resume effect) - the resubmit=1
              // flag tells it to open every step, not just jump to Bills,
              // and to finalize via the resubmit route instead of a plain
              // Draft finalize (see NewRequest.tsx's own handling of it).
              return (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => setViewRequestId(r.id)}
                  >
                    <Eye className="mr-1 h-3.5 w-3.5" /> View
                  </Button>
                  <DownloadReportButton travelRequestId={r.id} />
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={() =>
                      navigate(
                        `/my-requests/new?draftId=${encodeURIComponent(r.id)}&resubmit=1`
                      )
                    }
                  >
                    Edit & resubmit
                  </Button>
                </div>
              );
            }
            return (
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setViewRequestId(r.id)}
                >
                  <Eye className="mr-1 h-3.5 w-3.5" /> View
                </Button>
                <DownloadReportButton travelRequestId={r.id} />
              </div>
            );
          }
        })
      ]),
    [navigate]
  );

  const advanceColumns = React.useMemo(
    () =>
      advanceColumnHelper.columns([
        advanceColumnHelper.accessor((row) => tripRoute(row), {
          id: "country",
          meta: { label: "Trip" },
          header: ({ column }) => (
            <SortableHeader column={column}>Trip</SortableHeader>
          ),
          cell: ({ row }) => <TripCell trip={row.original} />
        }),
        advanceColumnHelper.accessor((row) => row.flightDetails || "", {
          id: "purpose",
          meta: { label: "Purpose" },
          header: "Purpose",
          cell: ({ row }) => (
            <span className="block max-w-[13.75rem] truncate text-sm" title={row.original.flightDetails || undefined}>
              {row.original.flightDetails || "-"}
            </span>
          )
        }),
        // See the Travel Requests table's identical column above - same
        // "Domestic/International alone doesn't say which scheme" gap.
        // Null on an advance raised before claimMethod existed (see its
        // doc comment in types.ts) - shown as "-" rather than guessing.
        advanceColumnHelper.accessor("claimMethod", {
          id: "claimMethod",
          meta: { label: "Claim method" },
          header: ({ column }) => (
            <SortableHeader column={column}>Claim method</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="text-sm">
              {row.original.claimMethod
                ? CLAIM_METHOD_LABEL[row.original.claimMethod]
                : "-"}
            </span>
          )
        }),
        advanceColumnHelper.display({
          id: "dates",
          meta: { label: "Travel dates" },
          header: "Travel dates",
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm">
              {formatDate(row.original.departureAt)} –{" "}
              {formatDate(row.original.arrivalAt)}
            </span>
          )
        }),
        // Sorts by the first currency line's amount (a multi-currency
        // advance has no single comparable total).
        advanceColumnHelper.accessor((row) => row.amounts[0]?.amount ?? 0, {
          id: "amount",
          meta: { label: "Amount" },
          header: ({ column }) => (
            <SortableHeader column={column}>Amount</SortableHeader>
          ),
          cell: ({ row }) => (
            <AdvanceAmountsCell amounts={row.original.amounts} />
          )
        }),
        advanceColumnHelper.accessor("status", {
          id: "status",
          meta: { label: "Status" },
          header: ({ column }) => (
            <SortableHeader column={column}>Status</SortableHeader>
          ),
          cell: ({ row }) => (
            <StatusBadge
              status={row.original.status}
              label={ADVANCE_STATUS_LABEL[row.original.status]}
            />
          )
        }),
        advanceColumnHelper.display({
          id: "linked",
          meta: { label: "Linked to" },
          header: "Linked to",
          cell: ({ row }) => {
            const summary = row.original.linkedRequestSummary;
            if (!summary) {
              return row.original.linkedRequestId ? (
                <span className="text-sm text-muted-foreground">
                  Linked request
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">-</span>
              );
            }
            return (
              <div className="text-sm">
                <p className="font-medium">{tripRoute(summary)}</p>
                <p className="text-xs text-muted-foreground">
                  {summary.status}
                </p>
              </div>
            );
          }
        }),
        advanceColumnHelper.accessor("createdAt", {
          id: "created",
          meta: { label: "Requested" },
          sortFn: "datetime",
          header: ({ column }) => (
            <SortableHeader column={column}>Requested</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {formatDate(row.original.createdAt)}
            </span>
          )
        }),
        advanceColumnHelper.display({
          id: "view",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const a = row.original;
            if (a.status === "Resubmit") {
              return (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => setViewAdvanceId(a.id)}
                  >
                    <Eye className="mr-1 h-3.5 w-3.5" /> View
                  </Button>
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={() => setEditingAdvanceId(a.id)}
                    disabled={editingAdvanceLoading}
                  >
                    Edit & resubmit
                  </Button>
                </div>
              );
            }
            return (
              <div className="text-right">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setViewAdvanceId(a.id)}
                >
                  <Eye className="mr-1 h-3.5 w-3.5" /> View
                </Button>
              </div>
            );
          }
        })
      ]),
    [editingAdvanceLoading]
  );

  return (
    <PageContainer fullWidth>
      <PageHeader
        title="Travel Reimbursements"
        description="Travel reimbursement requests and advances you have raised, and where each one stands."
        titleActions={
          <RefreshButton
            onRefresh={view === "requests" ? load : loadAdvances}
            isLoading={view === "requests" ? loading : advancesLoading}
          />
        }
      />

      <Tabs value={view} onValueChange={(v) => setView(v as View)}>
        <TabsList className="grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
          <TabsTrigger value="requests" className="min-w-0">
            {/* TabsTrigger (shadcn) forces whitespace-nowrap, which
               overflowed its grid-cols-2 cell on narrow phones and
               overlapped the Travel Advances trigger next to it - shorter
               copy there instead of trying to wrap. */}
            <span className="hidden sm:inline">Reimbursement Requests</span>
            <span className="truncate sm:hidden">Requests</span>
          </TabsTrigger>
          <TabsTrigger value="advances" className="min-w-0">
            <span className="hidden sm:inline">Travel Advances</span>
            <span className="truncate sm:hidden">Advances</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "requests" ? (
        <DataTable
          columns={columns}
          data={data}
          isLoading={loading}
          filterColumn="country"
          filterPlaceholder="Search destination…"
          emptyMessage={EMPTY_COPY[status]}
          persistKey="my-requests:travel"
          toolbarStart={
            // Narrower on phones than SegmentedTabs' own 180px default -
            // leaves enough room on the same toolbar row for "New request"
            // (toolbarEnd below) to sit beside it instead of wrapping to
            // its own right-aligned line underneath.
            <SegmentedTabs
              value={status}
              onChange={setStatus}
              className="h-9 w-[7.25rem] sm:w-[11.25rem]"
              options={[
                { value: "All", label: "All" },
                { value: "Draft", label: "Draft" },
                { value: "Submitted", label: "Submitted" },
                { value: "PendingFinance", label: "Pending" },
                { value: "Approved", label: "Approved" },
                { value: "Rejected", label: "Rejected" },
                { value: "Completed", label: "Completed" },
                { value: "ResubmitRequested", label: "Needs changes" }
              ]}
            />
          }
          toolbarEnd={
            <Button size="sm" className="h-9" onClick={handleNewRequestClick}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> New request
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={advanceColumns}
          data={advanceData}
          isLoading={advancesLoading}
          filterColumn="country"
          filterPlaceholder="Search destination…"
          emptyMessage={ADVANCE_EMPTY_COPY[advanceStatus]}
          persistKey="my-requests:advances"
          toolbarStart={
            // See the Travel Requests tab's toolbarStart above for why this
            // is narrower than SegmentedTabs' 180px default on phones.
            <SegmentedTabs
              value={advanceStatus}
              onChange={setAdvanceStatus}
              className="h-9 w-[7.25rem] sm:w-[11.25rem]"
              options={[
                { value: "All", label: "All" },
                { value: "Pending", label: "Pending" },
                { value: "Approved", label: "Approved" },
                { value: "Paid", label: "Paid" },
                { value: "Rejected", label: "Rejected" },
                { value: "Resubmit", label: "Needs changes" }
              ]}
            />
          }
          toolbarEnd={
            <Button
              size="sm"
              className="h-9"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Request advance
            </Button>
          }
        />
      )}

      <Sheet
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setError(null);
            setReviewOpen(false);
            resetAdvanceForm();
          }
        }}
      >
        <SheetContent className="flex w-full flex-col sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>
              {editingAdvanceId
                ? "Edit & resubmit advance"
                : "Request a travel advance"}
            </SheetTitle>
            <SheetDescription>
              {editingAdvanceId
                ? "Fix whatever needed changing, then resubmit - it goes straight back to whoever sent it back, no need to decide it again from scratch."
                : "Goes to your reporting manager for approval, then Finance for disbursement. When you later claim for reimbursement of this trip, you can link this advance to that request of the same type, and the travel details will fill in automatically."}
            </SheetDescription>
          </SheetHeader>
          <ScrollArea className="-mx-6 flex-1">
            <div className="space-y-3 px-6 py-1">
              {advResubmitNote && (
                <div className="flex items-start gap-3 rounded-md border border-[hsl(var(--quadra-orange))] bg-[hsl(var(--quadra-orange)/0.1)] p-3 text-sm">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                  <div className="space-y-0.5">
                    <p className="font-medium text-amber-700 dark:text-amber-400">
                      Changes requested by{" "}
                      {advResubmitNote.stage === "Finance"
                        ? "Finance"
                        : "your reporting manager"}
                    </p>
                    <p className="whitespace-pre-wrap text-foreground">
                      {advResubmitNote.comment}
                    </p>
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Designation</Label>
                <Input value={designation || "-"} disabled readOnly />
              </div>
              <div className="space-y-1.5">
                <Label>How will you claim this trip?</Label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(["fixed_allowance", "reimbursement"] as ClaimMethod[]).map(
                    (m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => chooseAdvClaimMethod(m)}
                        className={`rounded-md border p-3 text-left text-sm transition-colors ${
                          advClaimMethod === m
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <p className="font-medium">{CLAIM_METHOD_LABEL[m]}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {m === "fixed_allowance"
                            ? "Receive a predefined amount - no bills required (tax may apply)."
                            : "Claim actual expenses with bills, within policy limits."}
                        </p>
                      </button>
                    )
                  )}
                </div>
              </div>
              <TravelTypePicker
                value={advTravelType}
                onChange={chooseAdvTravelType}
              />
              {advTravelType && (
                <TripLocationFields
                  travelType={advTravelType}
                  value={advLocation}
                  onChange={setAdvLocation}
                  allowedCountries={allowedCountries}
                  loading={allowedCountriesLoading}
                  emptyHint={`No ${TRAVEL_TYPE_LABEL[advTravelType]} countries are configured for advances yet. Please contact your admin for configuration.`}
                  idPrefix="advance"
                  // The amount rows below show each currency themselves.
                  showCurrency={false}
                />
              )}
              {!editingAdvanceId && (
                <div className="space-y-1.5">
                  <Label>Finance Approver</Label>
                  <Combobox
                    value={advFinanceApproverId}
                    onChange={setAdvFinanceApproverId}
                    options={financeApprovers.map((a) => ({
                      value: a.id,
                      label: a.displayName || a.email || a.id,
                      sublabel: a.email || undefined
                    }))}
                    placeholder={
                      financeApproversLoading
                        ? "Loading…"
                        : financeApprovers.length === 0
                          ? "None configured"
                          : "Select a Finance Approver"
                    }
                    searchPlaceholder="Search…"
                    disabled={
                      financeApproversLoading || financeApprovers.length === 0
                    }
                  />
                  <p className="text-[0.6875rem] text-muted-foreground">
                    Only this person sees the advance in their Approver Queue
                    and can mark it paid - notified once it's approved, or
                    straight away if you have no reporting manager on file.
                  </p>
                </div>
              )}
              <div className="grid items-end gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Departure date &amp; time</Label>
                  <DateTimePicker
                    value={advDepartureAt}
                    onChange={setAdvDepartureAt}
                    minDate={new Date()}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Arrival date &amp; time</Label>
                  <DateTimePicker
                    value={advArrivalAt}
                    onChange={setAdvArrivalAt}
                    minDate={new Date()}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Purpose of travel</Label>
                <Textarea
                  value={advFlightDetails}
                  onChange={(e) => setAdvFlightDetails(e.target.value)}
                  placeholder="e.g. Client meeting, site visit"
                  className="resize-none"
                />
              </div>
              <AdvanceAmountsField
                value={advAmounts}
                onChange={setAdvAmounts}
                policyCurrency={advCurrency}
                limitsByCurrency={limitLoading ? null : advanceLimits}
                allowedCurrencies={advAllowedCurrencies}
                limitHint={
                  limitLoading ? (
                    <p className="text-xs text-muted-foreground">
                      Checking your policy limit…
                    </p>
                  ) : advanceLimitError ? (
                    <p className="text-xs text-destructive">
                      {advanceLimitError}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Choose the trip and enter departure and arrival to see
                      your policy limit.
                    </p>
                  )
                }
                idPrefix="advance"
              />
              <div className="space-y-1.5">
                <Label>Any Other Request (Optional)</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Initial accommodation and travel expenses"
                />
              </div>
              {error && !reviewOpen && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </div>
          </ScrollArea>
          <SheetFooter className="mt-4">
            <SheetClose>
              <Button className="w-full" variant="outline">Cancel</Button>
            </SheetClose>
            <Button onClick={handleSubmitAdvanceClick} disabled={saving}>
              {editingAdvanceId ? "Resubmit" : "Submit request"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Confirmation before the advance is actually submitted - a summary
          of what's being requested, per currency. */}
      <ConfirmDialog
        open={reviewOpen}
        onOpenChange={(open) => {
          if (saving) return;
          setReviewOpen(open);
          if (!open) setError(null);
        }}
        title={
          editingAdvanceId
            ? "Resubmit this advance request?"
            : "Submit this advance request?"
        }
        description={
          editingAdvanceId
            ? "It goes straight back to whoever sent it back for changes."
            : hasReportingManager
              ? "It goes to your reporting manager for approval, then Finance for disbursement."
              : "You have no reporting manager on file, so it goes straight to your Finance Approver."
        }
        confirmLabel={
          editingAdvanceId ? "Confirm & resubmit" : "Confirm & submit"
        }
        pending={saving}
        onConfirm={confirmSubmitAdvance}
      >
        <div className="space-y-1 rounded-md border bg-muted/50 p-3 text-sm">
          {advClaimMethod && (
            <SummaryRow
              label="Claim method"
              value={CLAIM_METHOD_LABEL[advClaimMethod]}
            />
          )}
          {advTravelType && advLocation.country && (
            <SummaryRow
              label={`Trip (${TRAVEL_TYPE_LABEL[advTravelType]})`}
              value={
                advTravelType === "international"
                  ? `${advLocation.originCountry} → ${advLocation.country}`
                  : `${advLocation.startCity} → ${advLocation.endCity}, ${advLocation.country}`
              }
            />
          )}
          {advDepartureAt && (
            <SummaryRow
              label="From"
              value={formatDateTime(advDepartureAt)}
            />
          )}
          {advArrivalAt && (
            <SummaryRow
              label="To"
              value={formatDateTime(advArrivalAt)}
            />
          )}
          {advAmounts.map((a) => (
            <SummaryRow
              key={a.currency}
              label={`Amount (${a.currency})`}
              value={`${a.currency} ${a.amount.toLocaleString()}`}
            />
          ))}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </ConfirmDialog>

      {financeNoticeOpen && (
        <FinanceNoticeDialog
          open
          onClose={() => {
            setFinanceNoticeOpen(false);
            setFinanceCc([]);
          }}
          toName={
            financeApprovers.find((a) => a.id === advFinanceApproverId)
              ?.displayName ?? null
          }
          toEmail={
            financeApprovers.find((a) => a.id === advFinanceApproverId)
              ?.email ?? null
          }
          cc={financeCc}
          onCcChange={setFinanceCc}
          onSend={() => submitAdvance(financeCc)}
          sending={saving}
          error={error}
        />
      )}

      <ConfirmDialog
        open={!!discardTarget}
        onOpenChange={(open) => !open && setDiscardTarget(null)}
        title="Discard this draft?"
        description="This deletes the draft and any bills already uploaded to it. If you linked an advance, it stays available to link again later."
        confirmLabel="Discard"
        destructive
        pending={discarding}
        onConfirm={confirmDiscard}
      />

      <Dialog open={showDraftReminder} onOpenChange={setShowDraftReminder}>
        <DialogContent className={PHONE_SHEET_DIALOG}>
          <DialogHeader>
            <DialogTitle>
              You have unfinished draft{" "}
              {draftRows.length === 1 ? "request" : "requests"}
            </DialogTitle>
            <DialogDescription>
              {draftRows.length === 1
                ? "You started a request that was never submitted. Continue it, or start a fresh one instead."
                : `You started ${draftRows.length} requests that were never submitted. Continue the most recent one, or start a fresh one instead.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {draftRows.slice(0, 5).map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  setShowDraftReminder(false);
                  navigate(
                    `/my-requests/new?draftId=${encodeURIComponent(r.id)}`
                  );
                }}
                className="w-full rounded-md border p-2.5 text-left text-sm transition-colors hover:bg-accent"
              >
                <p className="font-medium">{tripRoute(r)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(r.startDate)} –{" "}
                  {formatDate(r.endDate)} · created{" "}
                  {formatDate(r.createdAt)}
                </p>
              </button>
            ))}
            {draftRows.length > 5 && (
              <p className="text-xs text-muted-foreground">
                and {draftRows.length - 5} more - see the Draft filter below.
              </p>
            )}
          </div>
          <DialogFooter
            className={cn("sm:justify-between", PHONE_DIALOG_FOOTER)}
          >
            <Button
              variant="outline"
              onClick={() => {
                setShowDraftReminder(false);
                navigate("/my-requests/new?fresh=1");
              }}
            >
              Start a new one anyway
            </Button>
            <Button
              onClick={() => {
                setShowDraftReminder(false);
                navigate(
                  `/my-requests/new?draftId=${encodeURIComponent(draftRows[0].id)}`
                );
              }}
            >
              Continue most recent draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RequestDetailDialog
        travelRequestId={viewRequestId}
        open={!!viewRequestId}
        onOpenChange={(open) => {
          if (!open) {
            setViewRequestId(null);
            setHighlightOnOpen(false);
          }
        }}
        highlight={highlightOnOpen}
      />
      <AdvanceDetailDialog
        advanceId={viewAdvanceId}
        open={!!viewAdvanceId}
        onOpenChange={(open) => {
          if (!open) {
            setViewAdvanceId(null);
            setHighlightOnOpen(false);
          }
        }}
        highlight={highlightOnOpen}
      />
    </PageContainer>
  );
}
